import React, { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import _, { forEach } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Table, DatePicker, Divider } from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltDown, faArrowAltUp, faCloudDownload, faMinus } from '@fortawesome/pro-duotone-svg-icons';
import { useJsonToCsv } from 'react-json-csv';
import MainPage from '../../shared-components/main-page/MainPage';
import { Spinner } from '../../shared-components/Spinner';
import { H1, H3 } from '../../shared-components/typography/Title';
import Button from '../../shared-components/buttons/Button';
import Select from '../../shared-components/form/Select';
import Checkbox from '../../shared-components/form/Checkbox';
import { thousand, toCurrency, percentage, getVersionsForSelect } from '../../../functions';
import api from '../../../api/api';
import { notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import SummaryCardComparison from '../../shared-components/SummaryCardComparison';
import PopconfirmButton from '../../shared-components/buttons/PopconfirmButton';

function TransdevInvoicesCompare() {
  const { saveAsCsv } = useJsonToCsv();
  const { t, i18n } = useTranslation();
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedCompareToVersion, setSelectedCompareToVersion] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [useCompare, setUseCompare] = useState(false);

  const [loadingExportInvoice, setLoadingExportInvoice] = useState(false);
  const [loadingExportTrips, setLoadingExportTrips] = useState(false);

  function getTripsData(subsystem) {
    const subsystemKey = subsystem;
    const startDate = dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ssZ');
    const endDate = dayjs(selectedDate).add(1, 'month').add(-1, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
    const invoice = { ...summaryData.selectedInvoice };
    // Set loading
    for (let i = 0; i < invoice.serviceFees.length; i++) {
      const s = invoice.serviceFees[i];
      if (s.subsystem === subsystemKey) {
        s.loadingTrips = true;
      }  
    }
    setSummaryData({
      ...summaryData,
      selectedInvoice: invoice
    });
    const url = `transdev/invoices/trips/${subsystemKey}?startDate=${startDate}&endDate=${endDate}&version=${selectedVersion}`;
    api.get(url)
    .then((response) => {
      const tempInvoice = { ...summaryData.selectedInvoice };
      if (response.data.success) {
        for (let i = 0; i < tempInvoice.serviceFees.length; i++) {
          const s = tempInvoice.serviceFees[i];
          if (s.subsystem === subsystemKey) {
            s.allTrips = response.data.data;
          }  
        }
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
      for (let i = 0; i < tempInvoice.serviceFees.length; i++) {
        const s = tempInvoice.serviceFees[i];
        if (s.subsystem === subsystemKey) {
          s.loadingTrips = false;
        }  
      }
      setSummaryData({
        ...summaryData,
        selectedInvoice: tempInvoice
      });
    })
    .catch((error) => {
      const tempData2 = { ...summaryData.selectedInvoice };
      for (let i = 0; i < tempData2.serviceFees.length; i++) {
        const s = tempData2.serviceFees[i];
        if (s.subsystem === subsystemKey) {
          s.loadingTrips = false;
        }  
      }
      setSummaryData({
        ...summaryData,
        selectedInvoice: tempData2
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  const tripColumns = [
    {
      title: t('date'),
      key: 'serviceDate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{dayjs(row.serviceDate).format('MMM DD, YYYY')}</span>
      ),
    },
    {
      title: t('route'),
      key: 'routeId',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.routeId}</span>
      ),
    },
    {
      title: t('pattern'),
      key: 'patternId',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.patternId}</span>
      ),
    },
    {
      title: t('trip_start'),
      key: 'tripStart',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{dayjs(row.tripStart).format('hh:mm A')}</span>
      ),
    },
    {
      title: t('trip_end'),
      key: 'tripEnd',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{dayjs(row.tripEnd).format('hh:mm A')}</span>
      ),
    },
    {
      title: t('start_location'),
      key: 'startLocation',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.startLocation}</span>
      ),
    },
    {
      title: t('end_location'),
      key: 'endLocation',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.endLocation}</span>
      ),
    },
    {
      title: t('revenue_hours'),
      key: 'revenueHours',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{thousand(row.revenueHours)}</span>
      ),
    },
    {
      title: t('revenue_miles'),
      key: 'revenueMiles',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{thousand(row.revenueMiles)}</span>
      ),
    },
  ];
  const invoicesColumns = [
    // {
    //   title: t('date'),
    //   key: 'serviceDate',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <>
    //       {!row.isSummary && (<span>{dayjs(row.serviceDate).format('MMM, YYYY')}</span>)}
    //       {row.isSummary && ('')}
    //     </>
    //   ),
    // },
    {
      title: t('subsystem'),
      key: 'subsystem',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{row.subsystem}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('trips'),
      key: 'trips',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.trips)}</span>)}
          {row.isSummary && (<strong>{thousand(row.trips)}</strong>)}
        </>
      ),
    },
    {
      title: t('revenue_miles'),
      key: 'revenueMiles',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.revenueMiles)}</span>)}
          {row.isSummary && (<strong>{thousand(row.revenueMiles)}</strong>)}
        </>
      ),
    },
    {
      title: t('miles_rate'),
      key: 'milesRate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{toCurrency(row.milesRate)}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('revenue_hours'),
      key: 'revenueHours',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.revenueHours)}</span>)}
          {row.isSummary && (<strong>{thousand(row.revenueHours)}</strong>)}
        </>
      ),
    },
    {
      title: t('hours_rate'),
      key: 'hoursRate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{toCurrency(row.hoursRate)}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('subtotal'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{toCurrency(row.total)}</span>)}
          {row.isSummary && (<strong>{toCurrency(row.total)}</strong>)}
        </>
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'center',
      isSummary: true,
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (
            <Button
              className="bg-primaryColor mb-4 px-2 mt-2"
              textColor="text-primaryTextColor"
              loaderColor="bg-primaryTextColor"
              borderColor="border-primaryBorderColor"
              text={t('load_trips')}
              disabled={row.allTrips?.length > 0 || row.loadingTrips}
              loading={row.loadingTrips}
              onClick={() => getTripsData(row.subsystem)}
            />
          )}
          {row.isSummary && ('')}
        </>
      ),
    },
  ];
  const incidentalsColumns = [
    // {
    //   title: t('date'),
    //   key: 'date',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <>
    //       {!row.isSummary && (<span>{dayjs(`${row.year}-${row.month}-01`).format('MMM, YYYY')}</span>)}
    //       {row.isSummary && ('')}
    //     </>
    //   ),
    // },
    {
      title: t('description'),
      key: 'description',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{row.description}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('amount'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{toCurrency(row.total)}</span>)}
          {row.isSummary && (<strong>{toCurrency(row.total)}</strong>)}
        </>
      ),
    },
  ];
  const offsetColumns = [
    // {
    //   title: t('date'),
    //   key: 'date',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <>
    //       {!row.isSummary && (<span>{dayjs(`${row.date}-01`).format('MMM, YYYY')}</span>)}
    //       {row.isSummary && ('')}
    //     </>
    //   ),
    // },
    {
      title: t('subsystem'),
      key: 'subsystem',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{row.subsystem}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('checkpoints'),
      key: 'checkPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.checkPoints)}</span>)}
          {row.isSummary && (<strong>{thousand(row.checkPoints)}</strong>)}
        </>
      ),
    },
    {
      title: t('compliant_checkpoints'),
      key: 'compliantCheckPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.compliantCheckPoints)}</span>)}
          {row.isSummary && (<strong>{thousand(row.compliantCheckPoints)}</strong>)}
        </>
      ),
    },
    {
      title: t('non_compliant_checkpoints'),
      key: 'nonCompliantCheckPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{thousand(row.nonCompliantCheckPoints)}</span>)}
          {row.isSummary && (<strong>{thousand(row.nonCompliantCheckPoints)}</strong>)}
        </>
      ),
    },
    {
      title: t('checkpoints_otp'),
      key: 'checkPointsOTP',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span>{percentage(row.checkPointsOTP, 1)}</span>)}
          {row.isSummary && (<strong>{percentage(row.checkPointsOTP, 1)}</strong>)}
        </>
      ),
    },
    {
      title: t('compliance'),
      key: 'offsetPercentage',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span className={row.offsetPercentage < 0 ? 'text-red-400' : (row.offsetPercentage > 0 ? 'text-green-600' : '')}>{percentage(row.offsetPercentage, 1)}</span>)}
          {row.isSummary && ('')}
        </>
      ),
    },
    {
      title: t('offset'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          {!row.isSummary && (<span className={row.total < 0 ? 'text-red-400' : (row.total > 0 ? 'text-green-600' : '')}>{toCurrency(row.total)}</span>)}
          {row.isSummary && (<strong className={row.total < 0 ? 'text-red-400' : (row.total > 0 ? 'text-green-600' : '')}>{toCurrency(row.total)}</strong>)}
        </>
      ),
    },
  ];

  const invoicesCompareColumns = [
    // {
    //   title: t('date'),
    //   key: 'serviceDate',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <p>
    //       <span>{dayjs(row.selected?.serviceDate ?? row.compareTo?.serviceDate).format('MMM, YYYY')}</span>
    //     </p>
    //   ),
    // },
    {
      title: t('subsystem'),
      key: 'subsystem',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          {row.selected?.subsystem !== row.compareTo?.subsystem && (
            <>
              <span className="font-bold">{row.selected?.subsystem}</span>
              <span className="mx-2">:</span>
              <span>{row.compareTo?.subsystem}</span>
            </>
          )}
          {row.selected?.subsystem === row.compareTo?.subsystem && (
            <span>{row.selected?.subsystem}</span>
          )}
        </p>
      ),
    },
    {
      title: t('trips'),
      key: 'trips',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.trips, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.trips, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('revenue_miles'),
      key: 'revenueMiles',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.revenueMiles, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.revenueMiles, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('miles_rate'),
      key: 'milesRate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{toCurrency(row.selected?.milesRate, '0')}</span>
          <span className="mx-2">:</span>
          <span>{toCurrency(row.compareTo?.milesRate, '0')}</span>
        </p>
      ),
    },
    {
      title: t('revenue_hours'),
      key: 'revenueHours',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.revenueHours, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.revenueHours, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('hours_rate'),
      key: 'hoursRate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{toCurrency(row.selected?.hoursRate, '0')}</span>
          <span className="mx-2">:</span>
          <span>{toCurrency(row.compareTo?.hoursRate, '0')}</span>
        </p>
      ),
    },
    {
      title: t('subtotal'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{toCurrency(row.selected?.total, '0')}</span>
          <span className="mx-2">:</span>
          <span>{toCurrency(row.compareTo?.total, '0')}</span>
        </p>
      ),
    },
  ];
  const incidentalsCompareColumns = [
    // {
    //   title: t('date'),
    //   key: 'date',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <p>
    //       <span>{dayjs(row.selected?.serviceDate ?? row.compareTo?.serviceDate).format('MMM, YYYY')}</span>
    //     </p>
    //   ),
    // },
    {
      title: t('description'),
      key: 'description',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <>
          <p className="font-bold">{row.selected?.description}</p>
          <p>{row.compareTo?.description}</p>
        </>
      ),
    },
    {
      title: t('amount'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{toCurrency(row.selected?.total, '0')}</span>
          <span className="mx-2">:</span>
          <span>{toCurrency(row.compareTo?.total, '0')}</span>
        </p>
      ),
    },
  ];
  const offsetCompareColumns = [
    // {
    //   title: t('date'),
    //   key: 'date',
    //   align: 'center',
    //   className: 'text-sm',
    //   render: (row, object, index) => (
    //     <p>
    //       <span>{dayjs(row.selected?.serviceDate ?? row.compareTo?.serviceDate).format('MMM, YYYY')}</span>
    //     </p>
    //   ),
    // },
    {
      title: t('subsystem'),
      key: 'subsystem',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          {row.selected?.subsystem !== row.compareTo?.subsystem && (
            <>
              <span className="font-bold">{row.selected?.subsystem}</span>
              <span className="mx-2">:</span>
              <span>{row.compareTo?.subsystem}</span>
            </>
          )}
          {row.selected?.subsystem === row.compareTo?.subsystem && (
            <span>{row.selected?.subsystem}</span>
          )}
        </p>
      ),
    },
    {
      title: t('checkpoints'),
      key: 'checkPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.checkPoints, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.checkPoints, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('compliant_checkpoints'),
      key: 'compliantCheckPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.compliantCheckPoints, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.compliantCheckPoints, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('non_compliant_checkpoints'),
      key: 'nonCompliantCheckPoints',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{thousand(row.selected?.nonCompliantCheckPoints, 0, '0')}</span>
          <span className="mx-2">:</span>
          <span>{thousand(row.compareTo?.nonCompliantCheckPoints, 0, '0')}</span>
        </p>
      ),
    },
    {
      title: t('checkpoints_otp'),
      key: 'checkPointsOTP',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className="font-bold">{percentage(row.selected?.checkPointsOTP, 1, '0')}</span>
          <span className="mx-2">:</span>
          <span>{percentage(row.compareTo?.checkPointsOTP, 1, '0')}</span>
        </p>
      ),
    },
    {
      title: t('compliance'),
      key: 'offsetPercentage',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className={`font-bold ${row.selected?.offsetPercentage < 0 ? 'text-red-400' : (row.selected?.offsetPercentage > 0 ? 'text-green-600' : '')}`}>{percentage(row.selected?.offsetPercentage, 1, '0')}</span>
          <span className="mx-2">:</span>
          <span className={row.compareTo?.offsetPercentage < 0 ? 'text-red-400' : (row.compareTo?.offsetPercentage > 0 ? 'text-green-600' : '')}>{percentage(row.compareTo?.offsetPercentage, 1, '0')}</span>
        </p>
      ),
    },
    {
      title: t('offset'),
      key: 'total',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <p>
          <span className={`font-bold ${row.selected?.total < 0 ? 'text-red-400' : (row.selected?.total > 0 ? 'text-green-600' : '')}`}>{percentage(row.selected?.total, 1, '0')}</span>
          <span className="mx-2">:</span>
          <span className={row.compareTo?.total < 0 ? 'text-red-400' : (row.compareTo?.total > 0 ? 'text-green-600' : '')}>{percentage(row.compareTo?.total, 1, '0')}</span>
        </p>
      ),
    },
  ];

  function getVersions() {
    const startDate = dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ssZ');
    const endDate = dayjs(selectedDate).add(1, 'month').add(-1, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
    setLoadingVersions(true);
    api.get(`transdev/invoice/versions?startDate=${startDate}&endDate=${endDate}`)
    .then((response) => {
      if (response.data.success) {
        setVersions(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedVersion(response.data.data[0].version);
          setSelectedCompareToVersion(response.data.data[0].version);

          if (useCompare && response.data.data.length === 1) {
            setUseCompare(false);
          }
        }
      } else {
        setVersions([]);
      }
    })
    .catch((error) => {
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
    .finally(() => {
      setLoadingVersions(false);
    });
  }

  function getData() {
    setLoading(true);
    setSummaryData(null);
    const startDate = dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ssZ');
    const endDate = dayjs(selectedDate).add(1, 'month').add(-1, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
    const url = `transdev/invoices/compare?version=${selectedVersion}&versionCompareTo=${useCompare ? selectedCompareToVersion : ''}&startDate=${startDate}&endDate=${endDate}`;
    api.get(url)
    .then((response) => {
      if (response.data.success) {
        setHasSearched(true);
        setSummaryData(response.data.data);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
    .finally(() => {
      setLoading(false);
    });
  }

  function exportPdf(type, title) {
    setLoadingExportInvoice(true);
    const startDate = dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ssZ');
    const endDate = dayjs(selectedDate).add(1, 'month').add(-1, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
    const url = `transdev/invoices/pdf?type=${type}&startDate=${startDate}&endDate=${endDate}&version=${selectedVersion}`;
    api.get(url, { responseType: 'blob' })
      .then((response) => {
        setLoadingExportInvoice(false);
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `${title}_${selectedDate}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        console.error(error);
        setLoadingExportInvoice(false);
      });
  }

  useEffect(() => {
    setSelectedDate(dayjs().format('MMM YYYY'));
    setInitialLoad(false);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setSelectedVersion(null);
      setSelectedCompareToVersion(null);
      getVersions();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!useCompare && selectedVersion && selectedDate) {
      getData();
    } else if (useCompare && summaryData?.compareToInvoice) {
      setSummaryData({
        ...summaryData,
        compareToInvoice: null
      });
    }
  }, [selectedVersion, selectedDate]);

  return (
    <MainPage>
      <H1 text={`Transdev - ${t('invoices')}`} />
      {initialLoad && (
        <div className="w-full h-full flex items-center">
          <Spinner className="mx-auto text-accentColor" big />
        </div>
      )}
      {!initialLoad && (
        <>
          <div className="flex">
            {versions?.length > 1 && (
              <div>
                <Checkbox
                  label={t('compare_invoices')}
                  id="useCompare"
                  name="useCompare"
                  value={useCompare}
                  disabled={loading || (versions?.length <= 1)}
                  onChange={(obj) => {
                    setUseCompare(obj.target.checked);
                  }}
                />
              </div>
            )}
            <div className="flex space-x-2 ml-auto">
              <DatePicker
                disabled={loading}
                picker="month"
                className="w-36 h-11 mt-2 md:ml-2 lg:ml-0 xl:ml-2 bg-transparent"
                value={dayjs(selectedDate)}
                onChange={(obj) => {
                  setSelectedDate(obj.format('MMM YYYY'));
                }}
                format="MMM YYYY"
              />
              <Select
                className="w-48 h-11 mt-2 md:ml-2 lg:ml-0 xl:ml-2 bg-transparent"
                label={t('version')}
                id="version"
                name="version"
                value={selectedVersion}
                onChange={(obj) => {
                  setSelectedVersion(obj.target.value);
                }}
                loading={loadingVersions}
                disabled={loading || loadingVersions || versions.length === 0 || versions.length === 1}
                optGroups={getVersionsForSelect(versions, t).groups}
                options={getVersionsForSelect(versions, t).options}
              />
              {useCompare && (
                <Select
                  className="w-48 h-11 mt-2 md:ml-2 lg:ml-0 xl:ml-2 bg-transparent"
                  label={t('compare_to')}
                  id="comparetoversion"
                  name="comparetoversion"
                  value={selectedCompareToVersion}
                  onChange={(obj) => {
                    setSelectedCompareToVersion(obj.target.value);
                  }}
                  loading={loadingVersions}
                  disabled={loading || loadingVersions || versions.length === 0 || versions.length === 1}
                  optGroups={getVersionsForSelect(versions, t, true, selectedVersion).groups}
                  options={getVersionsForSelect(versions, t, true, selectedVersion).options}
                />
              )}
              <Button
                className="bg-primaryColor mb-4 px-2 mt-2"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                text={useCompare ? t('compare') : t('refresh')}
                loading={loading}
                disabled={useCompare && selectedVersion === selectedCompareToVersion}
                onClick={() => getData()}
              />
            </div>
          </div>

          {((!useCompare && hasSearched) || (useCompare && summaryData?.compareToInvoice)) && (
            <>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-4 w-full gap-4">
                <SummaryCardComparison
                  title={t('total_revenue_miles')}
                  loading={loading}
                  value1={thousand(summaryData?.selectedInvoice?.totalRevenueMiles)}
                  value2={useCompare ? thousand(summaryData?.compareToInvoice?.totalRevenueMiles) : null}
                  comparisonIcon={useCompare ? (summaryData?.differenceMarginRevenueMiles > 0 ? faArrowAltUp : (summaryData?.differenceMarginRevenueMiles < 0 ? faArrowAltDown : faMinus)) : null}
                  comparisonValue={useCompare ? thousand(summaryData?.differenceRevenueMiles) : null}
                  comparisonGrowth={useCompare ? percentage(summaryData?.differenceMarginRevenueMiles) : null}
                  comparisonIconColor={(summaryData?.differenceMarginRevenueMiles > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueMiles < 0 ? 'text-red-600' : null))}
                  comparisonValueColor={(summaryData?.differenceMarginRevenueMiles > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueMiles < 0 ? 'text-red-600' : null))}
                  comparisonGrowthColor={(summaryData?.differenceMarginRevenueMiles > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueMiles < 0 ? 'text-red-600' : null))}
                />
                <SummaryCardComparison
                  title={t('total_revenue_hours')}
                  loading={loading}
                  value1={thousand(summaryData?.selectedInvoice?.totalRevenueHours)}
                  value2={useCompare ? thousand(summaryData?.compareToInvoice?.totalRevenueHours) : null}
                  comparisonIcon={useCompare ? (summaryData?.differenceMarginRevenueHours > 0 ? faArrowAltUp : (summaryData?.differenceMarginRevenueHours < 0 ? faArrowAltDown : faMinus)) : null}
                  comparisonValue={useCompare ? thousand(summaryData?.differenceRevenueHours) : null}             
                  comparisonGrowth={useCompare ? percentage(summaryData?.differenceMarginRevenueHours) : null}
                  comparisonIconColor={summaryData?.differenceMarginRevenueHours > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueHours < 0 ? 'text-red-600' : null)}
                  comparisonValueColor={summaryData?.differenceMarginRevenueHours > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueHours < 0 ? 'text-red-600' : null)}
                  comparisonGrowthColor={summaryData?.differenceMarginRevenueHours > 0 ? 'text-accentColor' : (summaryData?.differenceMarginRevenueHours < 0 ? 'text-red-600' : null)}
                />
                <SummaryCardComparison
                  title={t('total')}
                  loading={loading}
                  value1={toCurrency(summaryData?.selectedInvoice?.total)}
                  value2={useCompare ? toCurrency(summaryData?.compareToInvoice?.total) : null}
                  comparisonIcon={useCompare ? (summaryData?.differenceMarginTotal > 0 ? faArrowAltUp : (summaryData?.differenceMarginTotal < 0 ? faArrowAltDown : faMinus)) : null}
                  comparisonValue={useCompare ? toCurrency(summaryData?.differenceTotal) : null}             
                  comparisonGrowth={useCompare ? percentage(summaryData?.differenceMarginTotal) : null}
                  comparisonIconColor={summaryData?.differenceMarginTotal > 0 ? 'text-accentColor' : (summaryData?.differenceMarginTotal < 0 ? 'text-red-600' : null)}
                  comparisonValueColor={summaryData?.differenceMarginTotal > 0 ? 'text-accentColor' : (summaryData?.differenceMarginTotal < 0 ? 'text-red-600' : null)}
                  comparisonGrowthColor={summaryData?.differenceMarginTotal > 0 ? 'text-accentColor' : (summaryData?.differenceMarginTotal < 0 ? 'text-red-600' : null)}
                />
              </div>

              {!useCompare && (summaryData?.selectedInvoice?.serviceFees?.length > 1 ||
                summaryData?.selectedInvoice?.specialFees?.length > 1 ||
                summaryData?.selectedInvoice?.offsets?.length > 1 ||
                summaryData?.selectedInvoice?.incidentals?.length > 1) && (
                <div className="flex mt-4">
                  <Button
                    className="ml-auto bg-primaryColor px-2"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="border-primaryBorderColor"
                    text={(
                      <div>
                        <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                        {t('export_invoice')} (PDF)
                      </div>
                    )}
                    loading={loadingExportInvoice}
                    onClick={() => {
                      exportPdf('', t('invoice'));
                    }}
                  />
                </div>
              )}

              <div className={(!useCompare && summaryData?.selectedInvoice?.serviceFees?.length > 1) ? '' : 'hidden'}>
                <Divider className="mt-6 pt-4" orientation="left">{t('regular_services')}</Divider>
                <Table
                  bordered
                  locale={{
                    emptyText: t('no_data')
                  }}
                  loading={loading}
                  pagination={false}
                  rowKey="uid"
                  dataSource={summaryData?.selectedInvoice?.serviceFees}
                  columns={invoicesColumns}
                  expandable={{
                    expandRowByClick: false,
                    rowExpandable: (record) => record.allTrips?.length > 0,
                    expandedRowRender: (record, index) => (
                      <Table
                        bordered
                        key={`${index}-${record.id}-${record.invoiceId}`}
                        size="small"
                        dataSource={record.allTrips}
                        columns={tripColumns}
                        title={() => (
                          <div className="flex">
                            <H3 text={`${t('total_trips')}: ${thousand(record?.allTrips?.length)}`} />
                            <Button
                              className="bg-primaryColor px-2 ml-auto mr-2"
                              textColor="text-primaryTextColor"
                              loaderColor="bg-primaryTextColor"
                              borderColor="border-primaryBorderColor"
                              text={(
                                <div>
                                  <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                                  {t('export_trips')} (CSV)
                                </div>
                              )}
                              loading={loading}
                              onClick={() => {
                                const tempFields = { };
                                for (let i = 0; i < tripColumns.length; i++) {
                                  tempFields[tripColumns[i].key] = tripColumns[i].title;
                                }
                                saveAsCsv({
                                  data: record.allTrips,
                                  fields: tempFields,
                                  filename: `${t('regular_services')}_${record.subsystem}_${t('trips')}_${selectedDate}`,
                                });
                              }}
                            />
                            <Button
                              className="bg-primaryColor px-2"
                              textColor="text-primaryTextColor"
                              loaderColor="bg-primaryTextColor"
                              borderColor="border-primaryBorderColor"
                              text={(
                                <div>
                                  <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                                  {t('export_trips')} (PDF)
                                </div>
                              )}
                              loading={loadingExportTrips}
                              onClick={() => {
                                setLoadingExportTrips(true);
                                const startDate = dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ssZ');
                                const endDate = dayjs(selectedDate).add(1, 'month').add(-1, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
                                api.get(`transdev/invoice/trips/${record.subsystem}/pdf?startDate=${startDate}&endDate=${endDate}`, { responseType: 'blob' })
                                  .then((response) => {
                                    setLoadingExportTrips(false);
                                    const href = URL.createObjectURL(response.data);
                                    const link = document.createElement('a');
                                    link.href = href;
                                    link.setAttribute('download', `${t('regular_services')}_${record.subsystem}_${t('trips')}_${selectedDate}.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(href);
                                  })
                                  .catch((error) => {
                                    console.error(error);
                                    setLoadingExportTrips(false);
                                  });
                              }}
                            />
                          </div>
                        )}
                      />
                    )
                  }}
                  title={() => (
                    <div className="flex space-x-2">
                      <Button
                        className="bg-primaryColor px-2 ml-auto"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (CSV)
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          const tempFields = { };
                          for (let i = 0; i < invoicesColumns.length; i++) {
                            tempFields[invoicesColumns[i].key] = invoicesColumns[i].title;
                          }
                          saveAsCsv({
                            data: _.filter(summaryData?.selectedInvoice?.serviceFees, (c) => !c.isSummary),
                            fields: tempFields,
                            filename: `${t('regular_services')}_${selectedDate}`,
                          });
                        }}
                      />
                      <Button
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (PDF)
                          </div>
                        )}
                        loading={loadingExportInvoice}
                        onClick={() => {
                          exportPdf('regular', t('regular_services'));
                        }}
                      />
                    </div>
                  )}
                />
              </div>
                
              <div className={!useCompare && summaryData?.selectedInvoice?.specialFees?.length > 1 ? '' : 'hidden'}>
                <Divider className="mt-6 pt-4" orientation="left">{t('special_fees')}</Divider>
                <Table
                  bordered
                  locale={{
                    emptyText: t('no_data')
                  }}
                  loading={loading}
                  pagination={false}
                  rowKey="id"
                  dataSource={summaryData?.selectedInvoice?.specialFees}
                  columns={_.filter(invoicesColumns, (c, i) => i !== invoicesColumns.length - 1)}
                  title={() => (
                    <div className="flex space-x-2">
                      <Button
                        className="bg-primaryColor px-2 ml-auto"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (CSV)
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          const tempFields = { };
                          for (let i = 0; i < invoicesColumns.length; i++) {
                            tempFields[invoicesColumns[i].key] = invoicesColumns[i].title;
                          }
                          saveAsCsv({
                            data: _.filter(summaryData?.selectedInvoice?.specialFees, (c) => !c.isSummary),
                            fields: tempFields,
                            filename: `${t('special_fees')}_${selectedDate}`,
                          });
                        }}
                      />
                      <Button
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (PDF)
                          </div>
                        )}
                        loading={loadingExportInvoice}
                        onClick={() => {
                          exportPdf('special', t('special_fees'));
                        }}
                      />
                    </div>
                  )}
                />
              </div>
                
              <div className={!useCompare && summaryData?.selectedInvoice?.offsets?.length > 1 ? '' : 'hidden'}>
                <Divider className="mt-6 pt-4" orientation="left">{t('offsets')}</Divider>
                <Table
                  bordered
                  locale={{
                    emptyText: t('no_data')
                  }}
                  pagination={false}
                  rowKey="id"
                  dataSource={summaryData?.selectedInvoice?.offsets}
                  columns={offsetColumns}
                  title={() => (
                    <div className="flex space-x-2">
                      <Button
                        className="bg-primaryColor px-2 ml-auto"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (CSV)
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          const tempFields = { };
                          for (let i = 0; i < offsetColumns.length; i++) {
                            tempFields[offsetColumns[i].key] = offsetColumns[i].title;
                          }
                          saveAsCsv({
                            data: _.filter(summaryData?.selectedInvoice?.offsets, (c) => !c.isSummary),
                            fields: tempFields,
                            filename: `${t('offsets')}_${selectedDate}`,
                          });
                        }}
                      />
                      <Button
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (PDF)
                          </div>
                        )}
                        loading={loadingExportInvoice}
                        onClick={() => {
                          exportPdf('offset', t('offsets'));
                        }}
                      />
                    </div>
                  )}
                />
              </div>
                
              <div className={!useCompare && summaryData?.selectedInvoice?.incidentals?.length > 1 ? '' : 'hidden'}>
                <Divider className="mt-6 pt-4" orientation="left">{t('other')}</Divider>
                <Table
                  bordered
                  locale={{
                    emptyText: t('no_data')
                  }}
                  loading={loading}
                  pagination={false}
                  rowKey="id"
                  dataSource={summaryData?.selectedInvoice?.incidentals}
                  columns={incidentalsColumns}
                  title={() => (
                    <div className="flex space-x-2">
                      <Button
                        className="bg-primaryColor px-2 ml-auto"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (CSV)
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          const tempFields = { };
                          for (let i = 0; i < incidentalsColumns.length; i++) {
                            tempFields[incidentalsColumns[i].key] = incidentalsColumns[i].title;
                          }
                          saveAsCsv({
                            data: _.filter(summaryData?.selectedInvoice?.incidentals, (c) => !c.isSummary),
                            fields: tempFields,
                            filename: `${t('other')}_${selectedDate}`,
                          });
                        }}
                      />
                      <Button
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
                            {t('export')} (PDF)
                          </div>
                        )}
                        loading={loadingExportInvoice}
                        onClick={() => {
                          exportPdf('other', t('other'));
                        }}
                      />
                    </div>
                  )}
                />
              </div>

              <div className={useCompare && summaryData?.compareToInvoice ? '' : 'hidden'}>
                <div className={summaryData?.showRegularServicesDifferences ? '' : 'hidden'}>
                  <Divider className="mt-6 pt-4" orientation="left">{t('regular_services')}</Divider>
                  <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-4 w-full gap-4">
                    <SummaryCardComparison
                      title={t('trips')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.regularServicesTrips)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.regularServicesTrips) : null}
                      comparisonValue={useCompare && summaryData?.regularServicesTripsDifferences !== 0 ? thousand(summaryData?.regularServicesTripsDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.regularServicesTripsDifferences > 0 ? faArrowAltUp : (summaryData?.regularServicesTripsDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.regularServicesTripsDifferences !== 0 ? percentage(summaryData?.regularServicesTripsMargin) : null}
                      comparisonIconColor={summaryData?.regularServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.regularServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.regularServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('revenue_miles')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.regularServicesRevenueMiles)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.regularServicesRevenueMiles) : null}
                      comparisonValue={useCompare && summaryData?.regularServicesRevenueMilesDifferences !== 0 ? thousand(summaryData?.regularServicesRevenueMilesDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.regularServicesRevenueMilesDifferences > 0 ? faArrowAltUp : (summaryData?.regularServicesRevenueMilesDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.regularServicesRevenueMilesDifferences !== 0 ? percentage(summaryData?.regularServicesRevenueMilesMargin) : null}
                      comparisonIconColor={summaryData?.regularServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.regularServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.regularServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('revenue_hours')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.regularServicesRevenueHours)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.regularServicesRevenueHours) : null}
                      comparisonValue={useCompare && summaryData?.regularServicesRevenueHoursDifferences !== 0 ? thousand(summaryData?.regularServicesRevenueHoursDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.regularServicesRevenueHoursDifferences > 0 ? faArrowAltUp : (summaryData?.regularServicesRevenueHoursDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.regularServicesRevenueHoursDifferences !== 0 ? percentage(summaryData?.regularServicesRevenueHoursMargin) : null}
                      comparisonIconColor={summaryData?.regularServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.regularServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.regularServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('subtotal')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.regularServicesSubtotal)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.regularServicesSubtotal) : null}
                      comparisonValue={useCompare && summaryData?.regularServicesSubtotalDifferences !== 0 ? thousand(summaryData?.regularServicesSubtotalDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.regularServicesSubtotalDifferences > 0 ? faArrowAltUp : (summaryData?.regularServicesSubtotalDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.regularServicesSubtotalDifferences !== 0 ? percentage(summaryData?.regularServicesSubtotalMargin) : null}
                      comparisonIconColor={summaryData?.regularServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.regularServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.regularServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.regularServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                    />
                  </div>
                  <div className={(summaryData?.serviceFees?.length > 1) ? '' : 'hidden'}>
                    <Table
                      bordered
                      locale={{
                        emptyText: t('no_data')
                      }}
                      loading={loading}
                      pagination={false}
                      dataSource={summaryData?.serviceFees}
                      columns={invoicesCompareColumns}
                    />
                  </div>
                </div>

                <div className={summaryData?.showSpecialServicesDifferences ? '' : 'hidden'}>
                  <Divider className="mt-6 pt-4" orientation="left">{t('special_fees')}</Divider>
                  <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-4 w-full gap-4">
                    <SummaryCardComparison
                      title={t('trips')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.specialServicesTrips)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.specialServicesTrips) : null}
                      comparisonValue={useCompare && summaryData?.specialServicesTripsDifferences !== 0 ? thousand(summaryData?.specialServicesTripsDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.specialServicesTripsDifferences > 0 ? faArrowAltUp : (summaryData?.specialServicesTripsDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.specialServicesTripsDifferences !== 0 ? percentage(summaryData?.specialServicesTripsMargin) : null}
                      comparisonIconColor={summaryData?.specialServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.specialServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.specialServicesTripsDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesTripsDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('revenue_miles')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.specialServicesRevenueMiles)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.specialServicesRevenueMiles) : null}
                      comparisonValue={useCompare && summaryData?.specialServicesRevenueMilesDifferences !== 0 ? thousand(summaryData?.specialServicesRevenueMilesDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.specialServicesRevenueMilesDifferences > 0 ? faArrowAltUp : (summaryData?.specialServicesRevenueMilesDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.specialServicesRevenueMilesDifferences !== 0 ? percentage(summaryData?.specialServicesRevenueMilesMargin) : null}
                      comparisonIconColor={summaryData?.specialServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.specialServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.specialServicesRevenueMilesDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueMilesDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('revenue_hours')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.specialServicesRevenueHours)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.specialServicesRevenueHours) : null}
                      comparisonValue={useCompare && summaryData?.specialServicesRevenueHoursDifferences !== 0 ? thousand(summaryData?.specialServicesRevenueHoursDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.specialServicesRevenueHoursDifferences > 0 ? faArrowAltUp : (summaryData?.specialServicesRevenueHoursDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.specialServicesRevenueHoursDifferences !== 0 ? percentage(summaryData?.specialServicesRevenueHoursMargin) : null}
                      comparisonIconColor={summaryData?.specialServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.specialServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.specialServicesRevenueHoursDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesRevenueHoursDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('revenue_hours')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.specialServicesSubtotal)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.specialServicesSubtotal) : null}
                      comparisonValue={useCompare && summaryData?.specialServicesSubtotalDifferences !== 0 ? thousand(summaryData?.specialServicesSubtotalDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.specialServicesSubtotalDifferences > 0 ? faArrowAltUp : (summaryData?.specialServicesSubtotalDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.specialServicesSubtotalDifferences !== 0 ? percentage(summaryData?.specialServicesSubtotalMargin) : null}
                      comparisonIconColor={summaryData?.specialServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.specialServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.specialServicesSubtotalDifferences > 0 ? 'text-accentColor' : (summaryData?.specialServicesSubtotalDifferences < 0 ? 'text-red-600' : null)}
                    />
                  </div>
                  <div className={(summaryData?.specialFees?.length > 1) ? '' : 'hidden'}>
                    <Table
                      bordered
                      locale={{
                        emptyText: t('no_data')
                      }}
                      loading={loading}
                      pagination={false}
                      dataSource={summaryData?.specialFees}
                      columns={invoicesCompareColumns}
                    />
                  </div>
                </div>

                <div className={summaryData?.showOffsetsDifferences ? '' : 'hidden'}>
                  <Divider className="mt-6 pt-4" orientation="left">{t('offsets')}</Divider>
                  <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-4 w-full gap-4">
                    <SummaryCardComparison
                      title={t('checkpoints')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.offsetsCheckpoints)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.offsetsCheckpoints) : null}
                      comparisonValue={useCompare ? thousand(summaryData?.offsetsCheckpointsDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.offsetsCheckpointsDifferences > 0 ? faArrowAltUp : (summaryData?.offsetsCheckpointsDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare ? percentage(summaryData?.offsetsCheckpointsMargin) : null}
                      comparisonIconColor={summaryData?.offsetsCheckpointsDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.offsetsCheckpointsDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.offsetsCheckpointsDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('compliant_checkpoints')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.offsetsCompliantCheckpoint)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.offsetsCompliantCheckpoint) : null}
                      comparisonValue={useCompare && summaryData?.offsetsCompliantCheckpointDifferences !== 0 ? thousand(summaryData?.offsetsCompliantCheckpointDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.offsetsCompliantCheckpointDifferences > 0 ? faArrowAltUp : (summaryData?.offsetsCompliantCheckpointDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.offsetsCompliantCheckpointDifferences !== 0 ? percentage(summaryData?.offsetsCompliantCheckpointMargin) : null}
                      comparisonIconColor={summaryData?.offsetsCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.offsetsCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.offsetsCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('non_compliant_checkpoints')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.offsetsNonCompliantCheckpoint)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.offsetsNonCompliantCheckpoint) : null}
                      comparisonValue={useCompare && summaryData?.offsetsNonCompliantCheckpointDifferences !== 0 ? thousand(summaryData?.offsetsNonCompliantCheckpointDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.offsetsNonCompliantCheckpointDifferences > 0 ? faArrowAltUp : (summaryData?.offsetsNonCompliantCheckpointDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.offsetsNonCompliantCheckpointDifferences !== 0 ? percentage(summaryData?.offsetsNonCompliantCheckpointMargin) : null}
                      comparisonIconColor={summaryData?.offsetsNonCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsNonCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.offsetsNonCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsNonCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.offsetsNonCompliantCheckpointDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsNonCompliantCheckpointDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('checkpoints_otp')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.offsetsCheckpointsOtp)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.offsetsCheckpointsOtp) : null}
                      comparisonValue={useCompare && summaryData?.offsetsCheckpointsOtpDifferences !== 0 ? thousand(summaryData?.offsetsCheckpointsOtpDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.offsetsCheckpointsOtpDifferences > 0 ? faArrowAltUp : (summaryData?.offsetsCheckpointsOtpDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.offsetsCheckpointsOtpDifferences !== 0 ? percentage(summaryData?.offsetsCheckpointsOtpMargin) : null}
                      comparisonIconColor={summaryData?.offsetsCheckpointsOtpDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsOtpDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.offsetsCheckpointsOtpDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsOtpDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.offsetsCheckpointsOtpDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsCheckpointsOtpDifferences < 0 ? 'text-red-600' : null)}
                    />
                    <SummaryCardComparison
                      title={t('offset')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.offsetsOffset)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.offsetsOffset) : null}
                      comparisonValue={useCompare && summaryData?.offsetsOffsetDifferences !== 0 ? thousand(summaryData?.offsetsOffsetDifferences) : null}
                      comparisonIcon={useCompare ? (summaryData?.offsetsOffsetDifferences > 0 ? faArrowAltUp : (summaryData?.offsetsOffsetDifferences < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.offsetsOffsetDifferences !== 0 ? percentage(summaryData?.offsetsOffsetMargin) : null}
                      comparisonIconColor={summaryData?.offsetsOffsetDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsOffsetDifferences < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.offsetsOffsetDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsOffsetDifferences < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.offsetsOffsetDifferences > 0 ? 'text-accentColor' : (summaryData?.offsetsOffsetDifferences < 0 ? 'text-red-600' : null)}
                    />
                  </div>
                  <div className={(summaryData?.offsets?.length > 1) ? '' : 'hidden'}>
                    <Table
                      bordered
                      locale={{
                        emptyText: t('no_data')
                      }}
                      loading={loading}
                      pagination={false}
                      dataSource={summaryData?.offsets}
                      columns={offsetCompareColumns}
                    />
                  </div>
                </div>

                <div className={summaryData?.showIncidentalsDifferences ? '' : 'hidden'}>
                  <Divider className="mt-6 pt-4" orientation="left">{t('other')}</Divider>
                  <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-4 w-full gap-4">
                    <SummaryCardComparison
                      title={t('amount')}
                      loading={loading}
                      value1={thousand(summaryData?.selectedInvoice?.incidentalsTotal)}
                      value2={useCompare ? thousand(summaryData?.compareToInvoice?.incidentalsTotal) : null}
                      comparisonValue={useCompare && summaryData?.incidentalsTotalDifference !== 0 ? thousand(summaryData?.incidentalsTotalDifference) : null}
                      comparisonIcon={useCompare ? (summaryData?.incidentalsTotalDifference > 0 ? faArrowAltUp : (summaryData?.incidentalsTotalDifference < 0 ? faArrowAltDown : faMinus)) : null}
                      comparisonGrowth={useCompare && summaryData?.incidentalsTotalDifference !== 0 ? percentage(summaryData?.incidentalsTotalMargin) : null}
                      comparisonIconColor={summaryData?.incidentalsTotalDifference > 0 ? 'text-accentColor' : (summaryData?.incidentalsTotalDifference < 0 ? 'text-red-600' : null)}
                      comparisonValueColor={summaryData?.incidentalsTotalDifference > 0 ? 'text-accentColor' : (summaryData?.incidentalsTotalDifference < 0 ? 'text-red-600' : null)}
                      comparisonGrowthColor={summaryData?.incidentalsTotalDifference > 0 ? 'text-accentColor' : (summaryData?.incidentalsTotalDifference < 0 ? 'text-red-600' : null)}
                    />
                  </div>
                  <div className={(summaryData?.incidentals?.length > 1) ? '' : 'hidden'}>
                    <Table
                      bordered
                      locale={{
                        emptyText: t('no_data')
                      }}
                      loading={loading}
                      pagination={false}
                      dataSource={summaryData?.incidentals}
                      columns={incidentalsCompareColumns}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          {!hasSearched && loading && (
            <div className="w-full h-full flex">
              <Spinner className="mx-auto mt-44 text-accentColor" big />
            </div>
          )}
        </>
      )}
    </MainPage>
  );
}

export default TransdevInvoicesCompare;
